import { CloudStorage } from 'react-native-cloud-storage';

import {
    getDeviceId,
    toLog
} from '../Backup';
import {
    getFileFromCloud,
    getFileList,
    getICloudFileName,
    ICLOUD_SCOPE
} from './iCloudFiles';


/**
 * Get metadata for a specific profile from iCloud.
 * @param profileId
 * @returns {Promise<{data: any|{}, fileName: string}>}
 */
export const getMetadata = async ({ profileId } = {}) => {
    if (!profileId) {
        throw new Error(Object.R('errors.noProfileIdForBackup'));
    }
    const fileName = getICloudFileName({
        profileId,
        type: 'metadata',
        deviceId: '',
    });
    const file = await getFileFromCloud({
        fileName,
        tryToDownload: true,
        withData: true,
        defaultData: '{}',
        timeout: 500,
        attempts: 20,
    });
    const result = {
        data: file?.data ? JSON.parse(file.data) : {},
        fileName,
    };
    toLog('ICloud:getMetadata', {
        file,
        fileName,
        result,
    });
    return result;
};

/**
 * Set metadata for a specific profile in iCloud.
 * This function updates the metadata with the current device ID and backup file name.
 * @param profileId
 * @param fileName
 * @param deviceId_
 * @returns {Promise<{deviceId: fileName}|null>}
 */
export const setMetadata = async ({
    profileId,
    fileName,
    deviceId: deviceId_,
} = {}) => {
    if (!profileId) {
        throw new Error(Object.R('errors.noProfileIdForBackup'));
    }
    try {
        const {
            data: metadata,
            fileName: metadataFileName,
        } = (await getMetadata({ profileId })) ?? {};
        toLog('ICloud:setMetadata:get', {
            metadata,
            metadataFileName,
            fileName,
        });
        const currentDeviceId = await getDeviceId();
        const deviceId = deviceId_ ?? currentDeviceId;
        // const data = metadata?.[deviceId]
        const oldKey = Object.entries(metadata)
            ?.find(([ , v ]) => v === fileName)?.[0];
        metadata[deviceId] = fileName;
        if (oldKey) {
            delete metadata?.[oldKey];
        }
        toLog('ICloud:setMetadata:set', {
            metadata,
            metadataFileName,
        });
        await CloudStorage.writeFile(metadataFileName, JSON.stringify(metadata), ICLOUD_SCOPE);
        return metadata;
    } catch (e) {
        toLog('ICloud:setMetadata:error', e);
        return null;
    }
};


/** * Get all metadata files from iCloud.
 * This function retrieves all metadata files that start with 'metadata_' or '.metadata_' from the iCloud AppData scope.
 * It returns an array of objects containing the parsed data and file names.
 * @returns {Promise<Array<{data: object, fileName: string}>>}
 */
export const getAllMetadataFiles = async () => {
    const files = await getFileList();
    const metadata = files.filter(file => file?.startsWith('metadata_') || file?.startsWith('.metadata_'));
    const metadataFiles = [];
    for (const fileName of metadata) {
        metadataFiles.push(await getFileFromCloud({
            fileName,
            withData: true,
            defaultData: '{}',
            tryToDownload: true,
            attempts: 20,
            timeout: 500,
        }));
    }
    return metadataFiles.map(file => ({
        data: JSON.parse(file.data),
        fileName: file.fileName,
    }));
};
